import { useState, useCallback } from 'react';
import type { UseFormTrigger } from 'react-hook-form';
import { client } from '../../services/api';
import { onboardPatientRogerIntakeIntakeIdOnboardPatientRogerPost } from '../../services/api/sdk.gen';
import type { RogerOnboardPatientResponse } from '../../services/api/types.gen';
import type { CreateEpisodeStepFormData } from '../../schemas/createEpisodeStepFormSchema';

interface UseRogerCreationOptions {
  intakeId: string | undefined;
  trigger: UseFormTrigger<CreateEpisodeStepFormData>;
}

interface UseRogerCreationReturn {
  creatingInRoger: boolean;
  rogerCreateResult: RogerOnboardPatientResponse | null;
  handleCreateInRoger: () => Promise<void>;
}

/**
 * Hook for creating patients in Roger.
 * Manages the creation state and handles the API call.
 */
export function useRogerCreation({
  intakeId,
  trigger,
}: UseRogerCreationOptions): UseRogerCreationReturn {
  const [creatingInRoger, setCreatingInRoger] = useState(false);
  const [rogerCreateResult, setRogerCreateResult] =
    useState<RogerOnboardPatientResponse | null>(null);

  const handleCreateInRoger = useCallback(async () => {
    if (!intakeId) return;

    // Validate form before calling API
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    setCreatingInRoger(true);
    setRogerCreateResult(null);

    try {
      const response =
        await onboardPatientRogerIntakeIntakeIdOnboardPatientRogerPost({
          client,
          path: { intake_id: intakeId },
        });

      if (response.data) {
        setRogerCreateResult(response.data);
      }
    } catch (error) {
      console.error('Error creating patient in Roger:', error);
      setRogerCreateResult({
        success: false,
        message: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setCreatingInRoger(false);
    }
  }, [intakeId, trigger]);

  return {
    creatingInRoger,
    rogerCreateResult,
    handleCreateInRoger,
  };
}
